<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import { refAutoReset } from '@vueuse/core';
import { updateToken } from '@/api/backend/_http';
import { useErhlCustomerLoanAppProcess } from '@/helpers/useErhlCustomerLoanAppProcess';
import { useAuth } from '@/helpers/useAuth';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { toast } from '@/helpers/toast';
import { AppValues } from '@/helpers/const';
import { getVerifyOtpErrorMessage } from '@/helpers/verifyOtpErrorMessage';

const { loanApp, saveAndGoNext, goBack } = useErhlCustomerLoanAppProcess();

const primaryApplicant = computed(
  () => loanApp.value.applicants.find((a) => a.type === 'primary') ?? loanApp.value.applicants[0],
);
const mobile = ref(primaryApplicant.value?.mobile ?? '');
const verificationCode = ref('');

const auth = useAuth();
const loading = ref(false);
const showSendCodeError = ref(false);
const highlightError = refAutoReset(false, 1000);

const sendOtpCode = async () => {
  const result = await auth.sendNewApplicantOtp({ mobilePhoneNumber: mobile.value });

  showSendCodeError.value = !result.success;
  if (result.success) {
    toast({ type: 'success', title: 'Success', description: 'One time password sent successfully' });
  } else {
    highlightError.value = true;
    toast({ type: 'danger', title: 'Unable to send code', description: 'Your OTP failed to send' });
  }
};

const verifyOtpCode = async () => {
  if (!primaryApplicant.value) return false;
  try {
    loading.value = true;
    const result = await auth.verifyNewApplicant(primaryApplicant.value, verificationCode.value);
    if (result.success) {
      updateToken(result.value);
      toast({ type: 'success', title: 'Success', description: 'Phone number verified' });
    } else {
      updateToken(null);
      toast({ type: 'danger', title: 'Unable to verify', description: getVerifyOtpErrorMessage(result.error) });
    }
    return result.success;
  } catch (err) {
    if (err) {
      toast({ type: 'danger', title: 'Unable to verify', description: 'Contact Support' });
    }
  } finally {
    loading.value = false;
  }
};

const saveAndContinue = async () => {
  if (await verifyOtpCode()) {
    return saveAndGoNext({ verificationCode: verificationCode.value });
  }
};

// Back up the application before submission, in case an error means it has to be submitted manually.
const { backupApplication } = useApplicationApi();
const MAX_BACKUP_ATTEMPTS = 5;
let backupRetryTimeout: ReturnType<typeof setTimeout> | undefined;
let isUnmounted = false;

function scheduleBackupRetry(attempt: number) {
  if (isUnmounted || attempt >= MAX_BACKUP_ATTEMPTS) return;
  backupRetryTimeout = setTimeout(() => backup(attempt + 1), 1000);
}

async function backup(attempt = 1) {
  try {
    const result = await backupApplication(loanApp.value);
    if (result?.data.message !== 'success') {
      scheduleBackupRetry(attempt);
    }
  } catch (error) {
    scheduleBackupRetry(attempt);
  }
}

onUnmounted(() => {
  isUnmounted = true;
  clearTimeout(backupRetryTimeout);
});

backup();
</script>

<template>
  <div>
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        Mobile number
        <BxHighlight>verification</BxHighlight>
      </h2>
    </div>
    <div>
      <div class="form-control flex">
        <BxInput
          v-model="mobile"
          inputmode="tel"
          label="Mobile number"
          label-type="floating"
          minlength="10"
          :maxlength="mobile.startsWith('+') ? 12 : 10"
          disabled
        />
        <BxButton
          id="verification-send-otp"
          data-test-id="verification-send-otp"
          class="ml-2 sm:ml-4"
          variant="secondary"
          @click="sendOtpCode()"
          >Send code</BxButton
        >
      </div>
      <div v-if="showSendCodeError" class="mb-8 mt-2 pr-2 text-rose-600">
        <p :class="{ 'animate-shakeX': highlightError }">
          Please check that your number has been entered correctly before resending the OTP code. If you are unable to
          proceed, please get in touch with us on <span class="font-medium">{{ AppValues.CONTACT }}</span> and quote
          your mobile phone number.
        </p>
      </div>
      <BxInput
        id="verification-otp-code"
        v-model="verificationCode"
        data-test-id="verification-otp-code"
        inputmode="numeric"
        class="form-control"
        label="6 digit verification code"
        label-type="floating"
        maxlength="6"
      />
    </div>
    <div>
      <ol
        class="otp-instruction mb-4 mt-4 rounded-md border-black px-4 py-4 text-sm text-bridgit-navy transition focus:outline-none focus-visible:ring-2 focus-visible:ring-bridgit-royalBlue focus-visible:ring-offset-2"
      >
        <h3>To complete your mobile number verification:</h3>
        <li>Click the "send code" button above.</li>
        <li>You'll receive a 6-digit verification code on your mobile.</li>
        <li>Enter the code in the provided box above.</li>
        <li>Click "Next" to complete the verification and continue your application.</li>
        <p>
          If you need help with this step please contact us at {{ AppValues.CONTACT }} or
          <a :href="`mailto:${AppValues.EMAIL}`">{{ AppValues.EMAIL }}</a>
        </p>
      </ol>
      <div class="flex gap-4">
        <BxButton
          id="verification-next"
          data-test-id="verification-next"
          variant="primary"
          :loading="loading"
          @click="saveAndContinue()"
          >Next</BxButton
        >
        <BxButton variant="link" data-test-id="verification-back" @click="goBack()">Back</BxButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.otp-instruction {
  background: #ebf1f5;

  li {
    list-style-type: decimal;
    margin-left: 2.1rem;
    line-height: 1.7rem;
  }

  a {
    font-weight: 500;
  }
}
</style>
